import React, { useState } from 'react';
import { Star } from 'lucide-react';
import SEO from '../components/SEO';
import useIsMobile from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_REVIEWS_API_URL || '';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.8rem 1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  fontSize: '0.95rem',
  color: 'var(--color-text)',
  background: 'var(--color-surface)',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.4rem',
  fontWeight: 600,
  color: 'var(--color-text-label)',
  fontSize: '0.85rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
};

const ReviewPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ name: '', title: '', facility: '', rating: '5', body: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [focused, setFocused] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch(`${API_URL}/reviews/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { setStatus('success'); window.scrollTo({ top: 0, behavior: 'smooth' }); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  const focusStyle = (name: string): React.CSSProperties => ({
    ...inputStyle,
    borderColor: focused === name ? 'var(--color-primary)' : 'var(--color-border)',
    boxShadow: focused === name ? '0 0 0 3px rgba(37,99,235,0.1)' : 'none',
  });

  return (
    <>
      <SEO
        title="Leave a Review"
        description="Share your experience working with JVP Health Tech Solutions."
        url="https://www.jvphealthtechsolutions.com/reviews"
      />
      <div className="page" style={{ paddingTop: '80px' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', textAlign: 'center' }}>
          <h1 style={{ color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem' }}>Leave a Review</h1>
          <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', opacity: 0.9, maxWidth: '560px', margin: '0 auto', lineHeight: 1.7 }}>
            We'd love to hear about your experience with JVP Health Tech Solutions.
          </p>
        </div>

        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: isMobile ? '3rem' : '5rem', alignItems: 'start' }}>

          {/* Sidebar */}
          <div>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Your Feedback Matters</h2>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '2rem' }}>
              Reviews help other healthcare facilities learn about our work and help us continue to improve.
            </p>
            <div style={{ background: 'linear-gradient(135deg, var(--color-navy-deep) 0%, var(--color-navy) 100%)', borderRadius: '14px', padding: '1.5rem' }}>
              <p style={{ color: 'var(--color-primary-pale)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Need to get in touch?</p>
              <p style={{ color: 'var(--color-muted-on-dark-light)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>Have a question or need support instead?</p>
              <a href="/contact" style={{ color: 'white', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none' }}>Contact Us →</a>
            </div>
          </div>

          {/* Form or success */}
          {status === 'success' ? (
            <div role="status" style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '2rem 1.25rem' : '3rem', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: '1px solid var(--color-surface-alt)', textAlign: 'center' }}>
              <div aria-hidden="true" style={{ width: '72px', height: '72px', background: 'var(--color-success-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✓</div>
              <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.75rem' }}>Thank You!</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '1.05rem', maxWidth: '380px', margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
                Your review has been submitted and is pending approval. We appreciate your feedback.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="/" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>Back to Home</a>
                <a href="/work" style={{ background: 'white', color: 'var(--color-primary)', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', border: '2px solid var(--color-primary)' }}>View Our Work</a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '2rem 1.25rem' : '2.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: '1px solid var(--color-surface-alt)', display: 'grid', gap: '1.5rem' }}>

              {/* Name + Title */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.25rem' }}>
                <div>
                  <label htmlFor="review-name" style={labelStyle}>Full Name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <input id="review-name" name="name" value={form.name} onChange={handleChange} required placeholder="Jane Smith"
                    style={focusStyle('name')} onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} />
                </div>
                <div>
                  <label htmlFor="review-title" style={labelStyle}>Job Title</label>
                  <input id="review-title" name="title" value={form.title} onChange={handleChange} placeholder="e.g. Charge Nurse"
                    style={focusStyle('title')} onFocus={() => setFocused('title')} onBlur={() => setFocused(null)} />
                </div>
              </div>

              {/* Facility */}
              <div>
                <label htmlFor="review-facility" style={labelStyle}>Facility / Organization <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input id="review-facility" name="facility" value={form.facility} onChange={handleChange} required placeholder="e.g. Memorial Hospital"
                  style={focusStyle('facility')} onFocus={() => setFocused('facility')} onBlur={() => setFocused(null)} />
              </div>

              {/* Star rating */}
              <div role="group" aria-labelledby="review-rating-label">
                <label id="review-rating-label" style={labelStyle}>Rating <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setForm({ ...form, rating: String(n) })}
                      aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      aria-pressed={Number(form.rating) === n}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}>
                      <Star size={28} strokeWidth={1.5}
                        fill={Number(form.rating) >= n ? 'var(--color-warning)' : 'none'}
                        color={Number(form.rating) >= n ? 'var(--color-warning)' : 'var(--color-text-muted)'} />
                    </button>
                  ))}
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', alignSelf: 'center', marginLeft: '0.5rem' }} aria-live="polite">
                    {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][Number(form.rating)]}
                  </span>
                </div>
              </div>

              {/* Review body */}
              <div>
                <label htmlFor="review-body" style={labelStyle}>Your Review <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <textarea id="review-body" name="body" value={form.body} onChange={handleChange} required rows={5}
                  placeholder="Tell us about your experience with JVP Health Tech Solutions..."
                  style={{ ...focusStyle('body'), resize: 'vertical' }} onFocus={() => setFocused('body')} onBlur={() => setFocused(null)} />
              </div>

              {status === 'error' && (
                <p role="alert" style={{ color: 'var(--color-danger-text)', fontSize: '0.875rem', background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: '8px', padding: '0.75rem 1rem', margin: 0 }}>
                  Something went wrong. Please try again.
                </p>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                <button type="submit" disabled={status === 'submitting'}
                  style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: '0.9rem 2.5rem', border: 'none', borderRadius: '50px', fontSize: '0.95rem', fontWeight: 700, cursor: status === 'submitting' ? 'not-allowed' : 'pointer', opacity: status === 'submitting' ? 0.7 : 1, letterSpacing: '0.02em' }}>
                  {status === 'submitting' ? 'Submitting…' : 'Submit Review →'}
                </button>
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', margin: 0 }}>Reviews are approved before publishing.</p>
              </div>

            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ReviewPage;
